/**
 * AnnotateBar — the light tool panel under the theater.
 *
 * The tool strip stays out of the picture: the stage shows nothing but the
 * frozen frame and the marks on it, and every control lives here, on the white
 * column below. Four rows — the head, the tools, the actions and the hint.
 *
 * It owns no annotation state. The tools dispatch into the shared annotation
 * reducer and the palette's open flag is lifted to the caller, because the
 * session's Escape stack has to close the palette before it discards the
 * capture.
 */
import { useCallback, useRef, type Dispatch } from 'react';

import { ArrowUpRight, ArrowUUpLeft, CaretDown, PencilSimple, Square, Trash } from '../../../../../design-system/icons';
import { useDocumentEvent } from '../../../../../lib/browser/useEventListener';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../ui/button';
import { FRAME_COLOURS, type AnnotationAction, type AnnotationState, type FrameColour, type Tool } from '../lib/annotationReducer';
import { formatTimecode } from '../../../../../lib/media/timecode';

const TOOL_ICON_PX = 17;
const ACTION_ICON_PX = 17;
const CARET_ICON_PX = 9;

const DRAW_PROMPT = 'Drag on the frame above to mark what needs changing';
const HEAD_LABEL = 'Marking up frame';
const SWATCH_LABEL = 'Mark colour';
const UNDO_LABEL = 'Undo';
const CLEAR_LABEL = 'Clear';
/** Distinct from the swatch: two controls sharing one name are ambiguous to AT. */
const PALETTE_LABEL = 'Mark colour options';

const TOOL_BUTTON_CLASS =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-[9px] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30 motion-reduce:transition-none';
/** The picked tool inverts to the ink colour of the panel. */
const TOOL_ACTIVE_CLASS = 'bg-foreground text-white hover:bg-foreground hover:text-white';
/** Compact action buttons: 32px tall, 13px label. */
const ACTION_BUTTON_CLASS = 'h-8 px-[14px] text-[13px]';
const COLOUR_DOT_SHADOW = 'shadow-[inset_0_0_0_1px_rgba(0,0,0,.18)]';

const TOOLS: Array<{ tool: Tool; label: string; Icon: typeof Square }> = [
  { tool: 'box', label: 'Box', Icon: Square },
  { tool: 'arrow', label: 'Arrow', Icon: ArrowUpRight },
  { tool: 'pen', label: 'Pen', Icon: PencilSimple },
];

export interface AnnotateBarProps {
  state: AnnotationState;
  dispatch: Dispatch<AnnotationAction>;
  /** Playhead the frame was grabbed at. Omitted where there is no capture behind the bar. */
  tMs?: number;
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  onAttach: () => void;
  onDiscard: () => void;
  /** Baking the frame — the actions are in flight and must not fire twice. */
  busy?: boolean;
}

function hintFor(markCount: number): string {
  if (markCount === 0) return DRAW_PROMPT;
  return `${markCount} mark${markCount === 1 ? '' : 's'} on this frame`;
}

/**
 * The colour popover. Deliberately not a Radix Popover: its
 * open flag belongs to the session so Escape closes it ahead of everything
 * else, and Radix would swallow that key itself.
 */
function ColourPalette({ selected, onSelect }: { selected: FrameColour; onSelect: (colour: FrameColour) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label={PALETTE_LABEL}
      data-testid="colour-palette"
      // `repeat(3,1fr)`, not `grid-cols-3`: Tailwind's is `minmax(0,1fr)`, which a
      // shrink-to-fit popover resolves to zero-width tracks — the swatches then
      // pile up on one another.
      className="absolute left-1/2 top-[38px] z-20 grid -translate-x-1/2 grid-cols-[repeat(3,1fr)] gap-[5px] rounded-xl border border-foreground bg-card p-[7px]"
    >
      <span aria-hidden="true" className="absolute -top-[5px] left-1/2 -ml-[5px] size-[9px] rotate-45 border-l border-t border-foreground bg-card" />
      {FRAME_COLOURS.map((colour) => (
        <button
          key={colour}
          type="button"
          role="radio"
          aria-checked={selected === colour}
          aria-label={`Colour ${colour}`}
          onClick={() => onSelect(colour)}
          className={cn(
            'flex size-[30px] items-center justify-center rounded-lg transition-colors duration-150 hover:bg-muted motion-reduce:transition-none',
            selected === colour && 'ring-2 ring-inset ring-foreground',
          )}
        >
          <span className={cn('block size-5 rounded-full', COLOUR_DOT_SHADOW)} style={{ backgroundColor: colour }} />
        </button>
      ))}
    </div>
  );
}

export function AnnotateBar({ state, dispatch, tMs, paletteOpen, setPaletteOpen, onAttach, onDiscard, busy = false }: AnnotateBarProps) {
  const swatchWrapRef = useRef<HTMLDivElement | null>(null);
  const markCount = state.shapes.length;
  // While Attach bakes the frame, the marks being baked must not change under it.
  const cannotEditMarks = markCount === 0 || busy;

  // The wrapper holds the swatch *and* the palette, so neither the toggling
  // click nor a colour pick ever counts as an "outside" click. Escape is not
  // handled here: the session's escape stack closes the palette first.
  function closeOnOutsideClick(event: MouseEvent): void {
    const { target } = event;
    if (target instanceof Node && swatchWrapRef.current?.contains(target)) return;
    setPaletteOpen(false);
  }
  useDocumentEvent('click', closeOnOutsideClick, { enabled: paletteOpen });

  const togglePalette = useCallback(() => setPaletteOpen(!paletteOpen), [paletteOpen, setPaletteOpen]);

  const selectColour = useCallback(
    (colour: FrameColour) => {
      dispatch({ type: 'colour', colour });
      setPaletteOpen(false);
    },
    [dispatch, setPaletteOpen],
  );

  const undo = useCallback(() => dispatch({ type: 'undo' }), [dispatch]);
  const clear = useCallback(() => dispatch({ type: 'clear' }), [dispatch]);

  return (
    <div className="mt-[14px] rounded-[14px] border border-border bg-card px-3 pb-3 pt-[11px]" data-testid="annotate-bar">
      <div className="mb-[10px] flex items-center gap-2">
        <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[.09em] text-muted-foreground">{HEAD_LABEL}</span>
        {tMs !== undefined && (
          <span className="rounded-full border border-border bg-muted px-[9px] py-px text-[12px] font-semibold tabular-nums text-foreground" data-testid="annotate-time">
            {formatTimecode(tMs)}
          </span>
        )}
      </div>

      <div className="mb-3 flex items-center gap-1">
        <div className="flex items-center gap-1" role="radiogroup" aria-label="Markup tool">
          {TOOLS.map(({ tool, label, Icon }) => (
            <button
              key={tool}
              type="button"
              role="radio"
              aria-checked={state.tool === tool}
              aria-label={label}
              onClick={() => dispatch({ type: 'tool', tool })}
              disabled={busy}
              className={cn(TOOL_BUTTON_CLASS, state.tool === tool && TOOL_ACTIVE_CLASS)}
            >
              <Icon size={TOOL_ICON_PX} />
            </button>
          ))}
        </div>

        <span aria-hidden="true" className="mx-1 h-[18px] w-px shrink-0 bg-border" />

        <div className="relative" ref={swatchWrapRef}>
          <button
            type="button"
            aria-label={SWATCH_LABEL}
            aria-haspopup="true"
            aria-expanded={paletteOpen}
            onClick={togglePalette}
            disabled={busy}
            className={cn(
              'inline-flex h-8 shrink-0 items-center gap-[3px] rounded-[9px] pl-[5px] pr-1.5 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30 motion-reduce:transition-none',
              paletteOpen && 'bg-muted text-foreground',
            )}
          >
            <span className={cn('size-[18px] rounded-full', COLOUR_DOT_SHADOW)} style={{ backgroundColor: state.colour }} />
            <CaretDown size={CARET_ICON_PX} weight="bold" />
          </button>
          {paletteOpen && <ColourPalette selected={state.colour} onSelect={selectColour} />}
        </div>

        <span className="flex-1" />

        <button type="button" aria-label={UNDO_LABEL} onClick={undo} disabled={cannotEditMarks} className={TOOL_BUTTON_CLASS}>
          <ArrowUUpLeft size={ACTION_ICON_PX} />
        </button>
        <button type="button" aria-label={CLEAR_LABEL} onClick={clear} disabled={cannotEditMarks} className={TOOL_BUTTON_CLASS}>
          <Trash size={ACTION_ICON_PX} />
        </button>
      </div>

      {/* The secondary action carries a border, which is shadcn's `outline`. */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDiscard} disabled={busy} className={ACTION_BUTTON_CLASS}>
          Discard
        </Button>
        <Button type="button" onClick={onAttach} disabled={busy} className={ACTION_BUTTON_CLASS}>
          Attach to note
        </Button>
      </div>

      <div className="mt-[10px] text-center text-[11px] text-muted-foreground" data-testid="annotate-hint">
        {hintFor(markCount)}
      </div>
    </div>
  );
}
